import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import {
  Card,
  Button,
  Table,
  Space,
  Typography,
  Spin,
  message,
  Statistic,
  Input,
  Tag,
  Tooltip,
  Popover,
  Tabs,
  Collapse,
  Empty,
  Alert
} from 'antd';
import {
  FilterOutlined,
  SortAscendingOutlined,
  SortDescendingOutlined,
  InfoCircleOutlined,
  SettingOutlined,
  FileTextOutlined,
  RedoOutlined,
  MenuFoldOutlined,
  MenuUnfoldOutlined
} from '@ant-design/icons';
import moment from 'moment';
import axios from '../../utils/axios';
import { useAuthCheck } from '../../hooks/useAuth';
import AffectedContactsModal from './AffectedContactsModal';
import AgentPromptButton from '../ai/AgentPromptButton';
import './analysis.css';
import './aggregation-table.css';
import { issue_explanations } from './AnalysisUtils';

const { Title, Text } = Typography;
const { Search } = Input;

const issue_names = {
  'cta_conflict': 'Conflicting CTAs',
  'frequency_issue': 'Same Tool',
  'communication_overlap': 'Multiple Tools',
};

// Helper functions

const formatCurrency = (cents, currency = 'usd') => {
  const amount = cents / 100;
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: currency.toUpperCase(),
    minimumFractionDigits: 2,
  }).format(amount);
};

const formatDurationFromSeconds = (totalSeconds) => {
  if (!totalSeconds || totalSeconds <= 0) return 'N/A';
  const days = Math.floor(totalSeconds / 86400);
  const hours = Math.floor((totalSeconds % 86400) / 3600);
  const minutes = Math.floor((totalSeconds % 3600) / 60);
  if (days > 0) return `${days}d ${hours}h`; // ${minutes}m`;
  if (hours > 0) return `${hours}h ${minutes}m`;
  return `${minutes}m`;
};

const AggregationAnalysisTablePage = () => {
  useAuthCheck();

  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  const [loading, setLoading] = useState(false);
  const [displayedResults, setDisplayedResults] = useState(null);
  const [displayedJob, setDisplayedJob] = useState(null);
  const [affectedContactsModalVisible, setAffectedContactsModalVisible] = useState(false);
  const [selectedContactIds, setSelectedContactIds] = useState([]);

  // Narrative sidekick panel state — generation is server-async: the GET/POST
  // narrative endpoints return immediately with status "generating"/"ready"/
  // "failed" rather than blocking on the LLM call, so this page polls while
  // "generating" instead of awaiting a single long-running request.
  const [narrativeFeatureEnabled, setNarrativeFeatureEnabled] = useState(false);
  const [narrativePanelCollapsed, setNarrativePanelCollapsed] = useState(true);
  const [narrative, setNarrative] = useState(null);
  const [narrativeLoading, setNarrativeLoading] = useState(false);
  const [narrativeError, setNarrativeError] = useState(null);
  const [narrativeRegenerating, setNarrativeRegenerating] = useState(false);
  const [narrativeElapsedSeconds, setNarrativeElapsedSeconds] = useState(0);

  const NARRATIVE_POLL_INTERVAL_MS = 3000;
  const NARRATIVE_STUCK_THRESHOLD_SECONDS = 60; // mirrors max(30, ai.llm.timeout_seconds) server-side

  useEffect(() => {
    let cancelled = false;
    axios.get('/ai/features')
      .then((response) => {
        if (!cancelled) setNarrativeFeatureEnabled(!!response.data?.narrative_enabled);
      })
      .catch(() => {
        // Feature-flag check failing should not surface an error — just keep
        // the panel hidden, same as "disabled".
      });
    return () => {
      cancelled = true;
    };
  }, []);

  // Separate insight data per tab
  const [conflictInsights, setConflictInsights] = useState(null);
  const [campaignInsights, setCampaignInsights] = useState(null);
  const [ctaInsights, setCtaInsights] = useState(null);

  // Tab state from URL
  const activeTab = searchParams.get('tab') || 'conflicts';

  // Table filtering and sorting state — per tab
  const [searchText, setSearchText] = useState('');

  // Insight ids selected from the narrative panel — filters all tabs by row id.
  const [narrativeInsightFilter, setNarrativeInsightFilter] = useState(new Set());

  // Conflicts tab sort
  const [conflictSortColumn, setConflictSortColumn] = useState('affected_contacts_count');
  const [conflictSortOrder, setConflictSortOrder] = useState('descend');
  const [typeFilter, setTypeFilter] = useState(null);

  // Campaigns tab sort
  const [campaignSortColumn, setCampaignSortColumn] = useState('mrr_cents');
  const [campaignSortOrder, setCampaignSortOrder] = useState('descend');

  // CTAs tab sort
  const [ctaSortColumn, setCtaSortColumn] = useState('mrr_cents');
  const [ctaSortOrder, setCtaSortOrder] = useState('descend');

  const event_source_names = {
    'hubspot_webhook_events': {'name': 'HubSpot', 'style': 'hubspot'},
    'esputnik_webhook_events': {'name': 'eSputnik', 'style': 'esputnik'},
    'stripe_webhook_events': {'name': 'Stripe', 'style': 'stripe'},
  };

  useEffect(() => {
    loadLatestInsights();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchParams]);

  const handleTabChange = (key) => {
    const newParams = new URLSearchParams(searchParams);
    newParams.set('tab', key);
    // Preserve jobId if present
    navigate(`/analysis?${newParams.toString()}`, { replace: true });
  };

  const loadLatestInsights = async () => {
    setLoading(true);
    try {
      const jobId = searchParams.get('jobId');
      const limit = searchParams.get('limit') || '100';

      // Get all jobs
      const jobsResponse = await axios.get('/analyze/aggregation/jobs');
      const jobs = jobsResponse.data;

      let targetJob = null;

      if (jobId) {
        targetJob = jobs.find(j => j.id === jobId);
      } else {
        // Get latest completed job
        const completed = jobs.filter(j => j.status === 'completed' && j.completed_at);
        if (completed.length > 0) {
          completed.sort((a, b) => new Date(b.completed_at) - new Date(a.completed_at));
          targetJob = completed[0];
        }
      }

      if (targetJob && targetJob.status === 'completed') {
        const conflictTypes = ['cta_conflict', 'frequency_issue', 'communication_overlap'];
        const conflictTypesParams = conflictTypes.map(t => `insight_types=${encodeURIComponent(t)}`).join('&');

        // Fetch results + all three insight groups in parallel
        const [resultsResponse, conflictsResponse, campaignsResponse, ctasResponse] = await Promise.all([
          axios.get(`/analyze/aggregation/jobs/${targetJob.id}/results`),
          axios.get(`/analyze/aggregation/jobs/${targetJob.id}/insights?limit=${limit}&${conflictTypesParams}`),
          axios.get(`/analyze/aggregation/jobs/${targetJob.id}/insights?limit=${limit}&insight_types=mrr_read_attribution`),
          axios.get(`/analyze/aggregation/jobs/${targetJob.id}/insights?limit=${limit}&insight_types=mrr_read_attribution_cta`),
        ]);

        setDisplayedResults(resultsResponse.data);
        setConflictInsights(conflictsResponse.data);
        setCampaignInsights(campaignsResponse.data);
        setCtaInsights(ctasResponse.data);
        // loadLatestInsights also re-runs on plain tab switches (searchParams
        // includes `tab`) — only reset narrative-related state when the job
        // actually changed, so switching tabs doesn't silently clear an
        // active filter or an open/generating narrative panel.
        if (displayedJob?.id !== targetJob.id) {
          setNarrativeInsightFilter(new Set());
          // Narrative is fetched lazily, only once the panel is actually opened
          // (see the toggle button and the narrativePanelCollapsed effect below)
          // — not proactively here, so viewing this page never auto-triggers an
          // LLM call for a job the user never asks to see a narrative for.
          setNarrative(null);
        }
        setDisplayedJob(targetJob);
      } else {
        message.info('No completed analysis jobs found');
        setNarrative(null);
      }
    } catch (error) {
      message.error('Failed to load analysis results');
      console.error('Error loading results:', error);
    } finally {
      setLoading(false);
    }
  };

  // ==========================================
  // Narrative sidekick panel
  // ==========================================

  // GET .../narrative auto-starts generation server-side the first time it's
  // called for a job with nothing usable yet, and always returns immediately
  // (status "generating"/"ready"/"failed") rather than blocking — this is
  // both the panel-open loader and the poll tick.
  const loadNarrative = async (jobId) => {
    try {
      const response = await axios.get(`/analyze/aggregation/jobs/${jobId}/narrative`);
      setNarrative(response.data);
      setNarrativeError(null);
    } catch (error) {
      if (error.response?.status === 400) {
        // Synthesis disabled server-side — treat like "no narrative", not an error.
        setNarrative(null);
      } else {
        setNarrativeError('Failed to load narrative');
        console.error('Error loading narrative:', error);
      }
    }
  };

  const openNarrativePanel = () => {
    if (!displayedJob) return;
    setNarrativePanelCollapsed(false);
    if (!narrative) {
      setNarrativeLoading(true);
      loadNarrative(displayedJob.id).finally(() => setNarrativeLoading(false));
    }
  };

  const regenerateNarrative = async () => {
    if (!displayedJob) return;
    setNarrativeRegenerating(true);
    setNarrativeError(null);
    try {
      const response = await axios.post(`/analyze/aggregation/jobs/${displayedJob.id}/regenerate-narrative`);
      setNarrative(response.data);
    } catch (error) {
      const detail = error.response?.data?.detail;
      const errorMessage = detail || 'Failed to regenerate narrative';
      setNarrativeError(errorMessage);
      message.error(errorMessage);
      console.error('Error regenerating narrative:', error);
    } finally {
      setNarrativeRegenerating(false);
    }
  };

  // Poll while generation is in flight; stop as soon as it lands on a
  // terminal state, the panel closes, or the job changes.
  useEffect(() => {
    if (narrativePanelCollapsed || narrative?.status !== 'generating' || !displayedJob) {
      return undefined;
    }
    const jobId = displayedJob.id;
    const interval = setInterval(() => {
      loadNarrative(jobId);
    }, NARRATIVE_POLL_INTERVAL_MS);
    return () => clearInterval(interval);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [narrativePanelCollapsed, narrative?.status, displayedJob?.id]);

  // Live elapsed-time counter while generating, so "stuck" detection has
  // something to compare against without re-fetching every second.
  useEffect(() => {
    if (narrative?.status !== 'generating' || !narrative?.started_at) {
      setNarrativeElapsedSeconds(0);
      return undefined;
    }
    const startedAt = new Date(narrative.started_at).getTime();
    const tick = () => setNarrativeElapsedSeconds(Math.max(0, Math.floor((Date.now() - startedAt) / 1000)));
    tick();
    const interval = setInterval(tick, 1000);
    return () => clearInterval(interval);
  }, [narrative?.status, narrative?.started_at]);

  const toggleNarrativeInsightFilter = (insightId) => {
    setNarrativeInsightFilter((prev) => {
      const next = new Set(prev);
      if (next.has(insightId)) {
        next.delete(insightId);
      } else {
        next.add(insightId);
      }
      return next;
    });
  };

  const applyNarrativeSectionFilter = (insightIds) => {
    setNarrativeInsightFilter(new Set(insightIds));
  };

  const clearNarrativeInsightFilter = () => setNarrativeInsightFilter(new Set());

  // ==========================================
  // Conflicts tab data
  // ==========================================

  const conflictTableData = useMemo(() => {
    if (!conflictInsights || !conflictInsights.data) return [];

    return conflictInsights.data.map((result, index) => ({
      key: index,
      id: result.id,
      insight_id: result.insight_id,
      type: result.insight_type,
      type_display: issue_names[result.insight_type] || (() => {
        const words = result.insight_type.replace('_', ' ').split(' ');
        if (words.length === 0) return '';
        const first = words[0].toUpperCase();
        const second = words[1] ? words[1].charAt(0).toUpperCase() + words[1].slice(1).toLowerCase() : '';
        return [first, second].filter(Boolean).join(' ');
      })(),
      severity: result.severity || 'Medium',
      description: result.description,
      affected_contacts_count: result.affected_contacts || 0,
      contact_ids: result.contact_ids || [],
      events_data: result.events_data || null,
      event_start: result.events_data?.event_start || null,
      event_trigger: result.events_data?.event_trigger || null,
      time_gap: (() => {
        if (result.events_data?.event_start?.timestamp && result.events_data?.event_trigger?.timestamp) {
          const start = moment(result.events_data.event_start.timestamp);
          const trigger = moment(result.events_data.event_trigger.timestamp);
          return trigger.diff(start);
        }
        return 0;
      })()
    }));
  }, [conflictInsights]);

  const processedConflictData = useMemo(() => {
    let filtered = [...conflictTableData];

    if (typeFilter) {
      filtered = filtered.filter(item => item.type === typeFilter);
    }

    if (narrativeInsightFilter.size > 0) {
      filtered = filtered.filter(item => narrativeInsightFilter.has(item.id));
    }

    if (searchText) {
      const searchLower = searchText.toLowerCase();
      filtered = filtered.filter(item =>
        item.type_display.toLowerCase().includes(searchLower) ||
        item.description?.toLowerCase().includes(searchLower) ||
        item.event_start?.subject?.toLowerCase().includes(searchLower) ||
        item.event_trigger?.subject?.toLowerCase().includes(searchLower) ||
        item.event_start?.description?.toLowerCase().includes(searchLower) ||
        item.event_trigger?.description?.toLowerCase().includes(searchLower) ||
        item.event_start?.content_group_name?.toLowerCase().includes(searchLower) ||
        item.event_trigger?.content_group_name?.toLowerCase().includes(searchLower)
      );
    }

    if (conflictSortColumn) {
      filtered.sort((a, b) => {
        let aVal = a[conflictSortColumn];
        let bVal = b[conflictSortColumn];
        if (aVal === undefined || aVal === null) aVal = 0;
        if (bVal === undefined || bVal === null) bVal = 0;
        if (typeof aVal === 'string' && typeof bVal === 'string') {
          aVal = aVal.toLowerCase();
          bVal = bVal.toLowerCase();
        }
        if (aVal === bVal) return 0;
        if (conflictSortOrder === 'descend') return aVal > bVal ? -1 : 1;
        return aVal > bVal ? 1 : -1;
      });
    }

    return filtered;
  }, [conflictTableData, searchText, conflictSortColumn, conflictSortOrder, typeFilter, narrativeInsightFilter]);

  const uniqueConflictTypes = useMemo(() => {
    return [...new Set(conflictTableData.map(item => item.type))];
  }, [conflictTableData]);

  // ==========================================
  // Campaigns tab data (mrr_attribution)
  // ==========================================

  const campaignTableData = useMemo(() => {
    if (!campaignInsights || !campaignInsights.data) return [];

    return campaignInsights.data.map((result, index) => ({
      key: index,
      id: result.id,
      insight_id: result.insight_id,
      type: result.insight_type,
      affected_contacts_count: result.affected_contacts || 0,
      contact_ids: result.contact_ids || [],
      event_start: result.events_data?.event_start || null,
      event_trigger: result.events_data?.event_trigger || null,
      aggregated_metrics: result.aggregated_metrics || {},
      mrr_cents: result.aggregated_metrics?.mrr_cents || 0,
      currency: result.aggregated_metrics?.currency || 'usd',
      time_to_conversion: result.aggregated_metrics?.time_difference_seconds || 0,
    }));
  }, [campaignInsights]);

  const processedCampaignData = useMemo(() => {
    let filtered = [...campaignTableData];

    if (narrativeInsightFilter.size > 0) {
      filtered = filtered.filter(item => narrativeInsightFilter.has(item.id));
    }

    if (searchText) {
      const searchLower = searchText.toLowerCase();
      filtered = filtered.filter(item =>
        item.event_start?.content_group_name?.toLowerCase().includes(searchLower) ||
        item.event_start?.subject?.toLowerCase().includes(searchLower) ||
        item.event_start?.description?.toLowerCase().includes(searchLower)
      );
    }

    if (campaignSortColumn) {
      filtered.sort((a, b) => {
        let aVal = a[campaignSortColumn];
        let bVal = b[campaignSortColumn];
        if (aVal === undefined || aVal === null) aVal = 0;
        if (bVal === undefined || bVal === null) bVal = 0;
        if (typeof aVal === 'string' && typeof bVal === 'string') {
          aVal = aVal.toLowerCase();
          bVal = bVal.toLowerCase();
        }
        if (aVal === bVal) return 0;
        if (campaignSortOrder === 'descend') return aVal > bVal ? -1 : 1;
        return aVal > bVal ? 1 : -1;
      });
    }

    return filtered;
  }, [campaignTableData, searchText, campaignSortColumn, campaignSortOrder, narrativeInsightFilter]);

  // ==========================================
  // CTAs tab data (mrr_attribution_cta)
  // ==========================================

  const ctaTableData = useMemo(() => {
    if (!ctaInsights || !ctaInsights.data) return [];

    return ctaInsights.data.map((result, index) => ({
      key: index,
      id: result.id,
      insight_id: result.insight_id,
      type: result.insight_type,
      affected_contacts_count: result.affected_contacts || 0,
      contact_ids: result.contact_ids || [],
      event_start: result.events_data?.event_start || null,
      event_trigger: result.events_data?.event_trigger || null,
      aggregated_metrics: result.aggregated_metrics || {},
      cta: result.events_data?.event_start?.cta || null,
      cta_text: result.events_data?.event_start?.cta_text || null,
      cta_url: result.events_data?.event_start?.cta_url || null,
      mrr_cents: result.aggregated_metrics?.mrr_cents || 0,
      currency: result.aggregated_metrics?.currency || 'usd',
      time_to_conversion: result.aggregated_metrics?.time_difference_seconds || 0,
    }));
  }, [ctaInsights]);

  const processedCtaData = useMemo(() => {
    let filtered = [...ctaTableData];

    if (narrativeInsightFilter.size > 0) {
      filtered = filtered.filter(item => narrativeInsightFilter.has(item.id));
    }

    if (searchText) {
      const searchLower = searchText.toLowerCase();
      filtered = filtered.filter(item =>
        item.cta?.toLowerCase().includes(searchLower) ||
        item.event_start?.content_group_name?.toLowerCase().includes(searchLower) ||
        item.event_start?.subject?.toLowerCase().includes(searchLower)
      );
    }

    if (ctaSortColumn) {
      filtered.sort((a, b) => {
        let aVal = a[ctaSortColumn];
        let bVal = b[ctaSortColumn];
        if (aVal === undefined || aVal === null) aVal = 0;
        if (bVal === undefined || bVal === null) bVal = 0;
        if (typeof aVal === 'string' && typeof bVal === 'string') {
          aVal = aVal.toLowerCase();
          bVal = bVal.toLowerCase();
        }
        if (aVal === bVal) return 0;
        if (ctaSortOrder === 'descend') return aVal > bVal ? -1 : 1;
        return aVal > bVal ? 1 : -1;
      });
    }

    return filtered;
  }, [ctaTableData, searchText, ctaSortColumn, ctaSortOrder, narrativeInsightFilter]);

  // ==========================================
  // MRR summary stats (always visible)
  // ==========================================

  const mrrSummaryStats = useMemo(() => {
    const campaigns = campaignInsights?.data || [];
    if (campaigns.length === 0) return null;
    const byCurrency = {};
    campaigns.forEach(insight => {
      const cents = insight.aggregated_metrics?.mrr_cents || 0;
      const cur = (insight.aggregated_metrics?.currency || 'usd').toLowerCase();
      if (!byCurrency[cur]) byCurrency[cur] = 0;
      byCurrency[cur] += cents;
    });
    const perCurrency = Object.entries(byCurrency)
      .filter(([, cents]) => cents > 0)
      .map(([currency, totalMrrCents]) => ({ currency, totalMrrCents }));
    return { perCurrency, campaignCount: campaigns.length };
  }, [campaignInsights]);

  // ==========================================
  // Sort handlers
  // ==========================================

  const handleConflictSort = (column) => {
    if (conflictSortColumn === column) {
      setConflictSortOrder(conflictSortOrder === 'ascend' ? 'descend' : 'ascend');
    } else {
      setConflictSortColumn(column);
      setConflictSortOrder('descend');
    }
  };

  const handleCampaignSort = (column) => {
    if (campaignSortColumn === column) {
      setCampaignSortOrder(campaignSortOrder === 'ascend' ? 'descend' : 'ascend');
    } else {
      setCampaignSortColumn(column);
      setCampaignSortOrder('descend');
    }
  };

  const handleCtaSort = (column) => {
    if (ctaSortColumn === column) {
      setCtaSortOrder(ctaSortOrder === 'ascend' ? 'descend' : 'ascend');
    } else {
      setCtaSortColumn(column);
      setCtaSortOrder('descend');
    }
  };

  const getSortIcon = (column, currentSortColumn, currentSortOrder) => {
    if (currentSortColumn !== column) return null;
    return currentSortOrder === 'ascend' ? <SortDescendingOutlined /> : <SortAscendingOutlined />;
  };

  // Helper function to calculate time gap between events
  const calculateTimeGap = (startTimestamp, triggerTimestamp) => {
    if (!startTimestamp || !triggerTimestamp) return 'N/A';

    const start = moment(startTimestamp);
    const trigger = moment(triggerTimestamp);
    const duration = moment.duration(trigger.diff(start));

    const days = Math.floor(duration.asDays());
    const hours = duration.hours();
    const minutes = duration.minutes();

    if (days > 0) {
      return `${days}d ${hours}h ${minutes}m`;
    } else if (hours > 0) {
      return `${hours}h ${minutes}m`;
    } else {
      return `${minutes}m`;
    }
  };

  // Render popover content for issue type explanation
  const renderIssueExplanationPopover = (issueType) => {
    const explanation = issue_explanations[issueType];
    if (!explanation) return null;

    return (
      <div style={{ maxWidth: '350px' }}>
        <div style={{ marginBottom: '12px' }}>
          <Text strong style={{ fontSize: '13px', display: 'block', marginBottom: '6px', color: '#1890ff' }}>
            {explanation.title}
          </Text>
          <Text strong style={{ fontSize: '11px', display: 'block', marginBottom: '4px' }}>
            Why it means:
          </Text>
          <Text style={{ fontSize: '12px', color: '#666', lineHeight: '1.5' }}>
            {explanation.description}
          </Text>
        </div>

        <div style={{ marginBottom: '12px' }}>
          <Text strong style={{ fontSize: '11px', display: 'block', marginBottom: '4px' }}>
            Why it matters:
          </Text>
          <ul style={{ fontSize: '11px', color: '#666', lineHeight: '1.4' }}>
            {explanation.impacts.map((impact, index) => <li key={index}>{impact}</li>)}
          </ul>
        </div>

        <div>
          <Text style={{ fontSize: '11px', fontStyle: 'italic', color: '#666', lineHeight: '1.4' }}>
            {explanation.recommendation}
          </Text>
        </div>
      </div>
    );
  };

  // Navigate to insight detail
  const navigateToInsight = (record) => {
    if (displayedJob?.id && record.id) {
      navigate(`/analysis?view=insight&jobId=${displayedJob.id}&insightId=${record.id}`);
    } else {
      setSelectedContactIds(record.contact_ids);
      setAffectedContactsModalVisible(true);
    }
  };

  // ==========================================
  // Conflicts table columns
  // ==========================================

  const conflictColumns = [
    {
      title: (
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '8px', cursor: 'pointer' }} onClick={() => handleConflictSort('type_display')}>
          <span>Issue Type</span>
          {getSortIcon('type_display', conflictSortColumn, conflictSortOrder)}
        </div>
      ),
      dataIndex: 'type_display',
      key: 'type',
      width: 140,
      render: (text, record) => (
        <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100%' }}>
          <div style={{ marginBottom: '8px', display: 'flex', alignItems: 'center', gap: '6px' }}>
            <Text strong style={{ fontSize: '14px' }}>{text}</Text>
            <Popover
              content={renderIssueExplanationPopover(record.type)}
              trigger="hover"
              placement="right"
              overlayStyle={{ maxWidth: '400px' }}
            >
              <InfoCircleOutlined
                className="issue-info-icon"
                style={{
                  color: '#1890ff',
                  fontSize: '14px',
                  cursor: 'pointer'
                }}
              />
            </Popover>
          </div>
        </div>
      ),
    },
    {
      title: <div style={{ textAlign: 'center' }}>Emails</div>,
      key: 'emails',
      render: (_, record) => (
        <div style={{ overflow: 'hidden' }}>
          {record.event_start && (
            <div style={{ marginBottom: '12px' }}>
              <div style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                gap: '8px',
                marginBottom: '2px',
                minWidth: 0,
              }}>
                <Tooltip title={record.event_start.subject || record.event_start.description}>
                  <span className="event-description">
                    {record.event_start.subject || record.event_start.description}
                  </span>
                </Tooltip>
                {record.event_start.cta && (
                  <Tag
                    style={{
                      backgroundColor: '#4285f4',
                      color: 'white',
                      border: 'none',
                      borderRadius: '12px',
                      fontSize: '8px',
                      fontWeight: '500',
                      flexShrink: 0,
                    }}
                  >
                    CTA: {record.event_start.cta}
                  </Tag>
                )}
              </div>
            </div>
          )}
          {record.event_trigger && (
            <div>
              <div style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                gap: '8px',
                minWidth: 0,
              }}>
                <span className="event-description">
                  {record.event_trigger.subject || record.event_trigger.description}
                </span>
                {record.event_trigger.cta && (
                  <Tag
                    style={{
                      backgroundColor: '#4285f4',
                      color: 'white',
                      border: 'none',
                      borderRadius: '12px',
                      fontSize: '8px',
                      fontWeight: '500',
                      flexShrink: 0,
                    }}
                  >
                    CTA: {record.event_trigger.cta}
                  </Tag>
                )}
              </div>
            </div>
          )}
        </div>
      ),
    },
    {
      title: <div style={{ textAlign: 'center' }}>Campaign</div>,
      key: 'campaign',
      render: (_, record) => (
        <div style={{ overflow: 'hidden' }}>
          {record.event_start && (
            <div style={{ marginBottom: '12px' }}>
              <div style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                gap: '8px',
                marginBottom: '4px',
                minWidth: 0,
              }}>
                {record.event_start.content_group_link ? (
                  <a
                    href={record.event_start.content_group_link}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="event-description event-description-link"
                  >
                    {record.event_start.content_group_name}
                  </a>
                ) : (
                  <Text className="event-description">
                    {record.event_start.content_group_name || 'N/A'}
                  </Text>
                )}
                <div style={{ display: 'flex', alignItems: 'center', flexShrink: 0 }}>
                <Tag className={`esp-tag esp-tag-${event_source_names[record.event_start.event_source]?.style || ''}`}>
                  {event_source_names[record.event_start.event_source]?.name || record.event_start.event_source}
                </Tag>
                <Tag
                  style={{
                    backgroundColor: '#007bff',
                    color: 'white',
                    border: 'none',
                    borderRadius: '12px',
                    fontSize: '8px',
                    fontWeight: '500'
                  }}
                >
                  {record.event_start.content_group_type}
                </Tag>
                </div>
              </div>
            </div>
          )}
          {record.event_trigger && (
            <div>
              <div style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                gap: '8px',
                marginBottom: '4px',
                minWidth: 0,
              }}>
                {record.event_trigger.content_group_link ? (
                  <a
                    href={record.event_trigger.content_group_link}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="event-description event-description-link"
                  >
                    {record.event_trigger.content_group_name}
                  </a>
                ) : (
                  <Text className="event-description">
                    {record.event_trigger.content_group_name || 'N/A'}
                  </Text>
                )}
                <div style={{ display: 'flex', alignItems: 'center', flexShrink: 0 }}>
                <Tag className={`esp-tag esp-tag-${event_source_names[record.event_trigger.event_source]?.style || ''}`}>
                  {event_source_names[record.event_trigger.event_source]?.name || record.event_trigger.event_source}
                </Tag>
                <Tag
                  style={{
                    backgroundColor: '#007bff',
                    color: 'white',
                    border: 'none',
                    borderRadius: '12px',
                    fontSize: '8px',
                    fontWeight: '500'
                  }}
                >
                  {record.event_trigger.content_group_type}
                </Tag>
                </div>
              </div>
            </div>
          )}
        </div>
      ),
    },
    {
      title: (
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '8px', cursor: 'pointer' }} onClick={() => handleConflictSort('time_gap')}>
          <span>Gap</span>
          {getSortIcon('time_gap', conflictSortColumn, conflictSortOrder)}
        </div>
      ),
      key: 'gap',
      width: 90,
      align: 'center',
      render: (_, record) => (
        <div className="time-gap-display">
          {calculateTimeGap(record.event_start?.timestamp, record.event_trigger?.timestamp)}
        </div>
      ),
    },
    {
      title: (
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '8px', cursor: 'pointer' }} onClick={() => handleConflictSort('affected_contacts_count')}>
          <span>Contacts</span>
          {getSortIcon('affected_contacts_count', conflictSortColumn, conflictSortOrder)}
        </div>
      ),
      dataIndex: 'affected_contacts_count',
      key: 'affected_contacts',
      width: 100,
      align: 'center',
      render: (count, record) => (
        <Tooltip title="Click to view insight details">
          <div
            style={{ cursor: 'pointer' }}
            onClick={() => navigateToInsight(record)}
          >
            <div className="affected-badge">
              {count}
            </div>
          </div>
        </Tooltip>
      ),
    },
  ];

  // ==========================================
  // Campaigns table columns (mrr_attribution)
  // ==========================================

  const campaignColumns = [
    {
      title: <div style={{ textAlign: 'center' }}>Campaign</div>,
      key: 'campaign',
      render: (_, record) => (
        <div>
          {record.event_start && (
            <div style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              gap: '8px'
            }}>
              {record.event_start.content_group_link ? (
                <a
                  href={record.event_start.content_group_link}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="event-description event-description-link"
                >
                  {record.event_start.content_group_name}
                </a>
              ) : (
                <Text style={{ fontSize: '14px', fontWeight: '500', color: '#4285f4' }}>
                  {record.event_start.content_group_name || 'N/A'}
                </Text>
              )}
              <div style={{ display: 'flex', alignItems: 'center', flexShrink: 0 }}>
                {record.event_start.event_source && event_source_names[record.event_start.event_source] && (
                  <Tag className={`esp-tag esp-tag-${event_source_names[record.event_start.event_source].style}`}>
                    {event_source_names[record.event_start.event_source].name}
                  </Tag>
                )}
                {record.event_start.content_group_type && (
                  <Tag
                    style={{
                      backgroundColor: '#007bff',
                      color: 'white',
                      border: 'none',
                      borderRadius: '12px',
                      fontSize: '8px',
                      fontWeight: '500'
                    }}
                  >
                    {record.event_start.content_group_type}
                  </Tag>
                )}
              </div>
            </div>
          )}
        </div>
      ),
    },
    {
      title: (
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '8px', cursor: 'pointer' }} onClick={() => handleCampaignSort('affected_contacts_count')}>
          <span>Contacts</span>
          {getSortIcon('affected_contacts_count', campaignSortColumn, campaignSortOrder)}
        </div>
      ),
      dataIndex: 'affected_contacts_count',
      key: 'affected_contacts',
      width: 100,
      align: 'center',
      render: (count, record) => (
        <Tooltip title="Click to view insight details">
          <div
            style={{ cursor: 'pointer' }}
            onClick={() => navigateToInsight(record)}
          >
            <div className="affected-badge">
              {count}
            </div>
          </div>
        </Tooltip>
      ),
    },
    {
      title: (
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '8px', cursor: 'pointer' }} onClick={() => handleCampaignSort('time_to_conversion')}>
          <span>Conversion</span>
          {getSortIcon('time_to_conversion', campaignSortColumn, campaignSortOrder)}
        </div>
      ),
      key: 'time_to_conversion',
      width: 110,
      align: 'center',
      render: (_, record) => (
        <div className="time-gap-display">
          {formatDurationFromSeconds(record.time_to_conversion)}
        </div>
      ),
    },
    {
      title: (
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '8px', cursor: 'pointer' }} onClick={() => handleCampaignSort('mrr_cents')}>
          <span>MRR</span>
          {getSortIcon('mrr_cents', campaignSortColumn, campaignSortOrder)}
        </div>
      ),
      key: 'mrr',
      width: 120,
      align: 'center',
      render: (_, record) => (
        <div className="mrr-amount">
          {record.mrr_cents > 0 ? formatCurrency(record.mrr_cents, record.currency) : 'N/A'}
        </div>
      ),
    },
  ];

  // ==========================================
  // CTAs table columns (mrr_attribution_cta)
  // ==========================================

  const ctaColumns = [
    {
      title: <div style={{ textAlign: 'center' }}>CTA</div>,
      key: 'cta',
      render: (_, record) => {
        const ctaLabel = record.cta_text || record.cta || 'Unknown CTA';
        return (
          <div style={{ overflow: 'hidden', minWidth: 0 }}>
            <Text strong style={{ fontSize: '14px' }}>{ctaLabel}</Text>
            {record.cta_url && (
              <div style={{ marginTop: '4px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                <Tooltip title={record.cta_url}>
                  <a
                    href={record.cta_url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="event-description event-description-link"
                  >
                    {record.cta_url}
                  </a>
                </Tooltip>
              </div>
            )}
          </div>
        );
      },
    },
    {
      title: (
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '8px', cursor: 'pointer' }} onClick={() => handleCtaSort('affected_contacts_count')}>
          <span>Contacts</span>
          {getSortIcon('affected_contacts_count', ctaSortColumn, ctaSortOrder)}
        </div>
      ),
      dataIndex: 'affected_contacts_count',
      key: 'affected_contacts',
      width: 100,
      align: 'center',
      render: (count, record) => (
        <Tooltip title="Click to view insight details">
          <div
            style={{ cursor: 'pointer' }}
            onClick={() => navigateToInsight(record)}
          >
            <div className="affected-badge">
              {count}
            </div>
          </div>
        </Tooltip>
      ),
    },
    {
      title: (
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '8px', cursor: 'pointer' }} onClick={() => handleCtaSort('time_to_conversion')}>
          <span>Conversion</span>
          {getSortIcon('time_to_conversion', ctaSortColumn, ctaSortOrder)}
        </div>
      ),
      key: 'time_to_conversion',
      width: 110,
      align: 'center',
      render: (_, record) => (
        <div className="time-gap-display">
          {formatDurationFromSeconds(record.time_to_conversion)}
        </div>
      ),
    },
    {
      title: (
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '8px', cursor: 'pointer' }} onClick={() => handleCtaSort('mrr_cents')}>
          <span>MRR</span>
          {getSortIcon('mrr_cents', ctaSortColumn, ctaSortOrder)}
        </div>
      ),
      key: 'mrr',
      width: 120,
      align: 'center',
      render: (_, record) => (
        <div className="mrr-amount">
          {record.mrr_cents > 0 ? formatCurrency(record.mrr_cents, record.currency) : 'N/A'}
        </div>
      ),
    },
  ];

  // ==========================================
  // Render
  // ==========================================

  if (loading) {
    return (
      <div className="analysis-page" style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '60vh' }}>
        <Spin size="large" />
      </div>
    );
  }

  return (
    <div className="analysis-page">
      {/* Header */}
      <div style={{ marginBottom: '24px' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <div>
            <Space align="center">
              <Title level={2} style={{ marginBottom: 0 }}>Campaign Intelligence</Title>
              <AgentPromptButton feature="intelligence" context={{ job_id: displayedJob?.id }} />
            </Space>
            <div>
              <Text type="secondary">
                Communication Overlap Analysis (24h Window)
              </Text>
            </div>
          </div>
          <Space>
            {narrativeFeatureEnabled && displayedJob && (
              <Tooltip title={narrativePanelCollapsed ? 'Show narrative panel' : 'Hide narrative panel'}>
                <Button
                  icon={narrativePanelCollapsed ? <MenuUnfoldOutlined /> : <MenuFoldOutlined />}
                  onClick={() => (narrativePanelCollapsed ? openNarrativePanel() : setNarrativePanelCollapsed(true))}
                  size="large"
                >
                  Narrative
                </Button>
              </Tooltip>
            )}
            <Button
              type="primary"
              icon={<SettingOutlined />}
              onClick={() => navigate('/analysis?view=jobs')}
              size="large"
            >
              Analysis
            </Button>
          </Space>
        </div>
      </div>

      {/* Statistics — always visible */}
      <div style={{ display: 'flex', gap: '16px', marginBottom: '12px', flexWrap: 'wrap' }}>
        {displayedResults && displayedResults.total_contacts_analyzed > 0 && (
          <div style={{ flex: '1 1 0', minWidth: '150px' }}>
            <Card className="insight-card">
              <Statistic
                title="Total recipients"
                value={displayedResults.total_contacts_analyzed}
                valueStyle={{ color: '#52c41a' }}
              />
            </Card>
          </div>
        )}
        {displayedResults && displayedResults.total_events_analyzed > 0 && (
          <div style={{ flex: '1 1 0', minWidth: '150px' }}>
            <Card className="insight-card">
              <Statistic
                title="Touchpoints analyzed"
                value={displayedResults.total_events_analyzed}
                valueStyle={{ color: '#52c41a' }}
              />
            </Card>
          </div>
        )}
        {displayedResults && displayedResults.total_insights > 0 && (
          <div style={{ flex: '1 1 0', minWidth: '150px' }}>
            <Card className="insight-card">
              <Statistic
                title="Issues found"
                value={displayedResults.total_insights}
                valueStyle={{ color: '#1890ff' }}
              />
            </Card>
          </div>
        )}
        {displayedResults && displayedResults.total_affected_contacts > 0 && (
          <div style={{ flex: '1 1 0', minWidth: '150px' }}>
            <Card className="insight-card">
              <Statistic
                title="Contacts affected"
                value={displayedResults.total_affected_contacts}
                valueStyle={{ color: '#52c41a' }}
              />
            </Card>
          </div>
        )}
        {mrrSummaryStats && mrrSummaryStats.perCurrency.map(({ currency, totalMrrCents }) => (
          <div key={currency} style={{ flex: '1 1 0', minWidth: '150px' }}>
            <Card className="insight-card">
              <Statistic
                title={`Attributed MRR (${currency.toUpperCase()})`}
                value={formatCurrency(totalMrrCents, currency)}
                valueStyle={{ color: '#52c41a' }}
              />
            </Card>
          </div>
        ))}
        {/*{mrrSummaryStats && mrrSummaryStats.campaignCount > 0 && (
          <div style={{ flex: '1 1 0', minWidth: '150px' }}>
            <Card className="insight-card">
              <Statistic
                title="Campaigns with MRR"
                value={mrrSummaryStats.campaignCount}
                valueStyle={{ color: '#1890ff' }}
              />
            </Card>
          </div>
        )}*/}
      </div>

      {/* Executive Narrative — collapsed by default, expands full-width here between the
          counters and the results table rather than as a persistent sidebar. */}
      {narrativeFeatureEnabled && displayedJob && !narrativePanelCollapsed && (
        <div style={{ marginBottom: '16px' }}>
          <Card
            className="insight-card"
            title={
              <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                <FileTextOutlined />
                <span>Executive Narrative</span>
              </div>
            }
            extra={
              <Space size="small">
                {narrative?.status === 'ready' && (
                  <Tooltip title="Regenerate narrative">
                    <Button
                      type="text"
                      size="small"
                      icon={<RedoOutlined />}
                      loading={narrativeRegenerating}
                      onClick={regenerateNarrative}
                    />
                  </Tooltip>
                )}
              </Space>
            }
          >
            {narrativeLoading ? (
              <div style={{ textAlign: 'center', padding: '32px 0' }}>
                <Spin />
              </div>
            ) : narrativeError ? (
              <Alert
                type="error"
                showIcon
                message={narrativeError}
                action={
                  <Button size="small" icon={<RedoOutlined />} loading={narrativeRegenerating} onClick={regenerateNarrative}>
                    Try again
                  </Button>
                }
              />
            ) : narrative?.status === 'generating' ? (
              <div style={{ textAlign: 'center', padding: '32px 0' }}>
                <Spin />
                <div style={{ marginTop: 12 }}>
                  <Text type="secondary" style={{ fontSize: 12 }}>
                    Generating narrative… ({narrativeElapsedSeconds}s)
                  </Text>
                </div>
                {narrativeElapsedSeconds > NARRATIVE_STUCK_THRESHOLD_SECONDS && (
                  <div style={{ marginTop: 12 }}>
                    <Text type="secondary" style={{ fontSize: 12, display: 'block', marginBottom: 8 }}>
                      This is taking longer than usual — it may be stuck.
                    </Text>
                    <Button size="small" icon={<RedoOutlined />} loading={narrativeRegenerating} onClick={regenerateNarrative}>
                      Try again
                    </Button>
                  </div>
                )}
              </div>
            ) : narrative?.status === 'failed' ? (
              <Empty
                image={Empty.PRESENTED_IMAGE_SIMPLE}
                description={
                  <span style={{ fontSize: '12px' }}>
                    {narrative.error || 'Narrative generation failed.'}
                  </span>
                }
              >
                <Button size="small" icon={<RedoOutlined />} loading={narrativeRegenerating} onClick={regenerateNarrative}>
                  Try again
                </Button>
              </Empty>
            ) : !narrative ? (
              <Empty
                image={Empty.PRESENTED_IMAGE_SIMPLE}
                description={
                  <span style={{ fontSize: '12px' }}>
                    No narrative for this job yet. Synthesis may be disabled, or generation hasn't completed.
                  </span>
                }
              >
                <Button size="small" icon={<RedoOutlined />} loading={narrativeRegenerating} onClick={regenerateNarrative}>
                  Generate
                </Button>
              </Empty>
            ) : (
              <div>
                <Text strong style={{ fontSize: '14px', display: 'block', marginBottom: '12px' }}>
                  {narrative.headline}
                </Text>
                {narrative.sections?.length > 0 && (
                  <Collapse
                    size="small"
                    ghost
                    defaultActiveKey={narrative.sections.map((_, idx) => idx)}
                    items={narrative.sections.map((section, idx) => ({
                      key: idx,
                      label: <Text strong style={{ fontSize: '13px' }}>{section.title}</Text>,
                      children: (
                        <div>
                          <Text style={{ fontSize: '12px' }}>{section.body}</Text>
                          {section.related_insight_ids?.length > 0 && (
                            <div style={{ marginTop: '8px' }}>
                              <Tooltip title="Filter the results tables to just this section's insights">
                                <Button
                                  size="small"
                                  type="link"
                                  style={{ fontSize: '11px', padding: 0, height: 'auto', marginBottom: '4px' }}
                                  icon={<FilterOutlined />}
                                  onClick={() => applyNarrativeSectionFilter(section.related_insight_ids)}
                                >
                                  Filter results to this section
                                </Button>
                              </Tooltip>
                              <div>
                                {section.related_insight_ids.map((id, i) => (
                                  <Tag
                                    key={id}
                                    style={{ fontSize: '10px', marginBottom: '4px', cursor: 'pointer' }}
                                    color={narrativeInsightFilter.has(id) ? 'blue' : 'default'}
                                    onClick={() => toggleNarrativeInsightFilter(id)}
                                  >
                                    Insight {i + 1}
                                  </Tag>
                                ))}
                              </div>
                            </div>
                          )}
                        </div>
                      ),
                    }))}
                  />
                )}
                {narrative.caveats?.length > 0 && (
                  <Alert
                    style={{ marginTop: '12px' }}
                    type="warning"
                    showIcon
                    message={<span style={{ fontSize: '12px' }}>Caveats</span>}
                    description={
                      <ul style={{ margin: 0, paddingLeft: '16px', fontSize: '11px' }}>
                        {narrative.caveats.map((caveat, idx) => (
                          <li key={idx}>{caveat}</li>
                        ))}
                      </ul>
                    }
                  />
                )}
                <Text type="secondary" style={{ fontSize: '10px', display: 'block', marginTop: '12px' }}>
                  Generated {moment(narrative.generated_at).format('YYYY-MM-DD HH:mm')}
                  {narrative.prompt_version ? ` · ${narrative.prompt_version}` : ''}
                </Text>
              </div>
            )}
          </Card>
        </div>
      )}

      {/* Tabs */}
      <Tabs
        activeKey={activeTab}
        onChange={handleTabChange}
        size="large"
        items={[
          {
            key: 'conflicts',
            label: `Conflicts${processedConflictData.length > 0 ? ` (${processedConflictData.length})` : ''}`,
            children: (
              <>
                {/* Conflicts Filters */}
                <Card className="insight-card" style={{ marginBottom: '16px' }}>
                  <Space size="middle" style={{ width: '100%', flexWrap: 'wrap' }}>
                    <Search
                      placeholder="Search by type, description, email subjects, or campaign names"
                      allowClear
                      style={{ width: 400 }}
                      value={searchText}
                      onChange={(e) => setSearchText(e.target.value)}
                      prefix={<FilterOutlined />}
                    />

                      <Text>Type:</Text>

                        <Button
                          size="small"
                          type={typeFilter === null ? 'primary' : 'default'}
                          style={{ fontSize: '10px' }}
                          onClick={() => setTypeFilter(null)}
                        >
                          All
                        </Button>
                        {uniqueConflictTypes.map(type => (
                          <Button
                            key={type}
                            size="small"
                            type={typeFilter === type ? 'primary' : 'default'}
                            style={{ fontSize: '10px' }}
                            onClick={() => setTypeFilter(type)}
                          >
                            {type.replace('_', ' ').split(' ').map(w =>
                              w.charAt(0).toUpperCase() + w.slice(1)
                            ).join(' ')}
                          </Button>
                        ))}

                    {narrativeInsightFilter.size > 0 && (
                      <Tag
                        closable
                        onClose={clearNarrativeInsightFilter}
                        color="blue"
                        icon={<FileTextOutlined />}
                      >
                        Filtered by narrative ({narrativeInsightFilter.size} insight{narrativeInsightFilter.size > 1 ? 's' : ''})
                      </Tag>
                    )}

                  </Space>
                </Card>

                {/* Conflicts Table */}
                <Card className="insight-card"
                  title={
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                      <span>Conflict Results</span>
                      {displayedJob && (
                        <Text type="secondary" style={{ fontSize: '14px' }}>
                          {displayedJob.name} - {moment(displayedJob.completed_at).format('YYYY-MM-DD HH:mm')}{displayedJob.contacts_threshold ? ` - ${displayedJob.contacts_threshold} contacts threshold` : ''}
                        </Text>
                      )}
                    </div>
                  }
                >
                  <Table
                    columns={conflictColumns}
                    dataSource={processedConflictData}
                    pagination={false}
                    size="middle"
                    bordered
                    tableLayout="fixed"
                    style={{ width: '100%' }}
                  />
                </Card>
              </>
            ),
          },
          {
            key: 'campaigns',
            label: `Campaigns${processedCampaignData.length > 0 ? ` (${processedCampaignData.length})` : ''}`,
            children: (
              <>
                {/* Campaigns Filter */}
                <Card className="insight-card" style={{ marginBottom: '16px' }}>
                  <Space size="middle" style={{ width: '100%', flexWrap: 'wrap' }}>
                    <Search
                      placeholder="Search by campaign name or email subject"
                      allowClear
                      style={{ width: 400 }}
                      value={searchText}
                      onChange={(e) => setSearchText(e.target.value)}
                      prefix={<FilterOutlined />}
                    />
                    {narrativeInsightFilter.size > 0 && (
                      <Tag
                        closable
                        onClose={clearNarrativeInsightFilter}
                        color="blue"
                        icon={<FileTextOutlined />}
                      >
                        Filtered by narrative ({narrativeInsightFilter.size} insight{narrativeInsightFilter.size > 1 ? 's' : ''})
                      </Tag>
                    )}
                  </Space>
                </Card>

                {/* Campaigns Table */}
                <Card className="insight-card"
                  title={
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                      <span>Campaign MRR Attribution</span>
                      {displayedJob && (
                        <Text type="secondary" style={{ fontSize: '14px' }}>
                          {displayedJob.name} - {moment(displayedJob.completed_at).format('YYYY-MM-DD HH:mm')}
                        </Text>
                      )}
                    </div>
                  }
                >
                  <Table
                    columns={campaignColumns}
                    dataSource={processedCampaignData}
                    pagination={false}
                    size="middle"
                    bordered
                    tableLayout="fixed"
                    style={{ width: '100%' }}
                  />
                </Card>
              </>
            ),
          },
          {
            key: 'ctas',
            label: `CTAs${processedCtaData.length > 0 ? ` (${processedCtaData.length})` : ''}`,
            children: (
              <>
                {/* CTAs Filter */}
                <Card className="insight-card" style={{ marginBottom: '16px' }}>
                  <Space size="middle" style={{ width: '100%', flexWrap: 'wrap' }}>
                    <Search
                      placeholder="Search by CTA, campaign name, or email subject"
                      allowClear
                      style={{ width: 400 }}
                      value={searchText}
                      onChange={(e) => setSearchText(e.target.value)}
                      prefix={<FilterOutlined />}
                    />
                    {narrativeInsightFilter.size > 0 && (
                      <Tag
                        closable
                        onClose={clearNarrativeInsightFilter}
                        color="blue"
                        icon={<FileTextOutlined />}
                      >
                        Filtered by narrative ({narrativeInsightFilter.size} insight{narrativeInsightFilter.size > 1 ? 's' : ''})
                      </Tag>
                    )}
                  </Space>
                </Card>

                {/* CTAs Table */}
                <Card className="insight-card"
                  title={
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                      <span>CTA MRR Attribution</span>
                      {displayedJob && (
                        <Text type="secondary" style={{ fontSize: '14px' }}>
                          {displayedJob.name} - {moment(displayedJob.completed_at).format('YYYY-MM-DD HH:mm')}
                        </Text>
                      )}
                    </div>
                  }
                >
                  <Table
                    columns={ctaColumns}
                    dataSource={processedCtaData}
                    pagination={false}
                    size="middle"
                    bordered
                    tableLayout="fixed"
                    style={{ width: '100%' }}
                  />
                </Card>
              </>
            ),
          },
        ]}
      />

      {/* Affected Contacts Modal */}
      <AffectedContactsModal
        visible={affectedContactsModalVisible}
        onCancel={() => setAffectedContactsModalVisible(false)}
        contactIds={selectedContactIds}
        title="Affected Customers"
      />
    </div>
  );
};

export default AggregationAnalysisTablePage;
